import CommitmentFollowupBadge from './CommitmentFollowupBadge'
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { stageCommitmentState } from '../lib/optimisticCommitmentState'
import {
  Archive, ArchiveRestore,
  Ban, Bell, BellOff, CheckSquare, Circle, CircleCheck, Clock,
  Edit2, MoreVertical, ChevronDown, ChevronRight, PencilLine,
  RotateCcw,
} from 'lucide-react'
import { useLongPress } from '../lib/longPress'
import {
  useUpdateTopic, useSnoozeTopic, usePrefetchChat, type Topic, type CommitmentTopicItem,
} from '../lib/api'
import { TODO_CHAT_ID } from '../lib/chatNavigation'
import {
  applyCommitmentAction, completeCommitmentGroup, formatLocalDateTime,
  type CommitmentFollowupScope,
} from '../lib/commitmentActions'
import { preloadThreadViewComponentsForTopic } from '../lib/preloadChatViews'
import { startNavTiming } from '../lib/perfTelemetry'
import { fullDateTimeLabel, relativeTimeFromNow, shortDateTimeLabel } from '../lib/time'
import { topicDisplayTitle } from '../lib/topicTitles'
import CommitmentFollowupScopeDialog from './CommitmentFollowupScopeDialog'

const COMMITMENTS_CHAT_ID = 'pwa-auto-commitments'

type Props = {
  chatId: string
  topic: Topic
  draft?: string | null
  section: 'active' | 'snoozed' | 'done'
  onOpen: () => void
  onSnoozePick: () => void          // opens the SnoozeCalendar modal
  onMarkDone: (topic: Topic) => Promise<boolean>  // parent owns confirmation + undo lifecycle
  onReopen: (topic: Topic) => void
  /** Only meaningful for rows backed by an external source (a Todoist task).
   *  Omitted by lists whose topics have no source to block, e.g. the Code tab. */
  onBlockSource?: (topic: Topic) => void
  /** Renders immediately left of the row body — the Code tab puts the agent
   *  badge here so a mixed Claude/Codex list is readable at a glance. */
  leadingBadge?: React.ReactNode
  /** Force the always-visible left action rail (Close / Wake / Reopen) on. The
   *  Todo list turns it on by chat id; the Code tab asks for it explicitly. */
  inlineQuickActions?: boolean
  /** Coding rows only: render the row action as a labelled Archive icon on the
   *  RIGHT instead of the left-hand Circle "Mark done". The automation groups
   *  keep the left placement deliberately (see the comment on that block). */
  compact?: boolean
  archiveStyle?: boolean
  todoistHierarchy?: {
    depth: number
    hasChildren: boolean
    expanded: boolean
    onToggleExpanded?: () => void
  }
  selectionMode?: boolean
  selected?: boolean
  canSelect?: boolean
  onSelect?: () => void
  onToggleSelected?: () => void
}

function isTodoistTopic(topic: Topic): boolean {
  return topic.source_kind === 'todoist' || topic.source_kind === 'todoist_task'
}

function sourceLabel(topic: Topic): string | null {
  if (isTodoistTopic(topic)) return null
  if (topic.source_kind === 'freelancer') return 'Freelancer'
  if (topic.source_kind === 'telegram_imported') return 'From Telegram'
  return null
}

function previewDraft(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

function compactRelativeAge(epochSeconds: number): string {
  const ageSeconds = Math.max(0, Math.floor(Date.now() / 1000) - epochSeconds)
  if (ageSeconds < 45) return 'now'
  if (ageSeconds < 3600) {
    const minutes = Math.max(1, Math.round(ageSeconds / 60))
    return `${minutes}m`
  }
  if (ageSeconds < 86_400) {
    const hours = Math.max(1, Math.round(ageSeconds / 3600))
    return `${hours}h`
  }
  if (ageSeconds < 2_629_746) {
    const days = Math.max(1, Math.round(ageSeconds / 86_400))
    return `${days}d`
  }
  if (ageSeconds < 31_556_952) {
    const months = Math.max(1, Math.round(ageSeconds / 2_629_746))
    return `${months}mo`
  }
  const years = Math.max(1, Math.round(ageSeconds / 31_556_952))
  return `${years}y`
}

export default function GroupTopicRow({
  chatId,
  topic,
  draft,
  section,
  onOpen,
  onSnoozePick,
  onMarkDone,
  onReopen,
  onBlockSource,
  leadingBadge,
  inlineQuickActions = false,
  archiveStyle = false,
  compact = false,
  todoistHierarchy,
  selectionMode = false,
  selected = false,
  canSelect = false,
  onSelect,
  onToggleSelected,
}: Props) {
  const qc = useQueryClient()
  const updateTopic = useUpdateTopic(chatId)
  const snoozeTopic = useSnoozeTopic(chatId)
  const prefetchChat = usePrefetchChat()
  const [actionsOpen, setActionsOpen] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [renameDraft, setRenameDraft] = useState(topic.title || '')
  const [commitmentFollowupFor, setCommitmentFollowupFor] = useState<CommitmentTopicItem | null>(null)
  const [chooseFollowupJob, setChooseFollowupJob] = useState(false)
  const [commitmentPending, setCommitmentPending] = useState<Record<string, 'done' | 'setfu'>>({})
  const [commitmentError, setCommitmentError] = useState<string | null>(null)
  const suppressNextClickRef = useRef(false)
  // Optimistic tick: the active row's empty circle fills with a check the
  // instant it's tapped (before the row slides to Done). Reset if the close is
  // undone (topic.closed flips back to false).
  const [optimisticDone, setOptimisticDone] = useState(false)
  useEffect(() => {
    if (!topic.closed) setOptimisticDone(false)
  }, [topic.closed])

  // Long-press starts multi-select for open topics; closed topics keep
  // the old action-menu behavior.
  const longPressBind = useLongPress((e) => {
    e.preventDefault()
    suppressNextClickRef.current = true
    if (canSelect && onSelect) {
      setActionsOpen(false)
      onSelect()
      return
    }
    setActionsOpen(true)
  }, { delay: 450 })

  const source = sourceLabel(topic)
  const hasDraft = !!draft?.trim()
  const displayTitle = topicDisplayTitle(topic)
  // Gate on the Todo list itself (the chat), NOT on source_kind === todoist:
  // every row here is a task Mark triages — whether it came from Todoist or was
  // added by hand (null / telegram_imported source) — so the checkbox + time
  // rail show uniformly. Close just closes the topic when there's no Todoist
  // task behind it. Scoped to this one chat so other groups (X bookmark,
  // YouTube, email), which have their OWN distinct in-topic actions, are
  // untouched.
  const showFullTodoistTaskTitle = chatId === TODO_CHAT_ID
  const unread = topic.unread ?? 0
  const topicHasUnreadMessages = unread > 0
  const topicPrimaryTextTone = topicHasUnreadMessages
    ? 'font-semibold text-stone-950 dark:text-stone-50'
    : 'font-normal text-stone-700 dark:text-stone-200'
  const showInlineQuickActions = showFullTodoistTaskTitle || inlineQuickActions
  const showTodoistHierarchy = !!todoistHierarchy
  const todoistTreeIndent = Math.min(Math.max(todoistHierarchy?.depth ?? 0, 0), 6) * 14
  const commitmentItems = (topic.commitment_items ?? []).filter((item) => item.status !== 'done' && item.status !== 'canceled')
  const showCommitmentItems = chatId === COMMITMENTS_CHAT_ID && commitmentItems.length > 0

  const lastTs = topic.last_message_ts
  const wakeAt = topic.snoozed_until
  const topicAgeTs = (section === 'done' && topic.closed_at) ? topic.closed_at : (lastTs ?? topic.created_at)
  const topicAge = compactRelativeAge(topicAgeTs)

  // Email rows surface the true received date/time + a body snippet so
  // identically-titled threads (the recurring "RE: Worklist Checkpoint"
  // problem) are distinguishable at a glance. received_at is the email's own
  // timestamp; fall back to created_at (spawn time, ~1-2 min later) only if
  // the brief's Received line was unparseable.
  const isEmailTopic = topic.source_kind === 'email_inbound'
  const emailReceivedTs = isEmailTopic ? (topic.received_at ?? topic.created_at) : null
  const emailPreview = topic.email_preview?.trim() || null

  function commitRename() {
    const t = renameDraft.trim()
    if (t && t !== topic.title) {
      updateTopic.mutate({ topic_id: topic.id, title: t })
    }
    setRenaming(false)
  }

  const startPrefetch = () => {
    startNavTiming('topic-open', `${chatId}:${topic.id}`, {
      source: 'group-topic-row',
    })
    preloadThreadViewComponentsForTopic({
      chatId,
      sourceKind: topic.source_kind,
    })
    prefetchChat(chatId, topic.id)
  }

  const invalidateCommitmentRows = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['topics', chatId] }),
      qc.invalidateQueries({ queryKey: ['commitments-dashboard'] }),
      qc.invalidateQueries({ queryKey: ['chats'] }),
    ])
  }

  const runCommitmentDone = async (item: CommitmentTopicItem) => {
    setCommitmentPending((current) => ({ ...current, [item.id]: 'done' }))
    setCommitmentError(null)
    try {
      await applyCommitmentAction({ action: 'done', commitmentId: item.id }, qc)
      void invalidateCommitmentRows()
    } catch (e) {
      setCommitmentError(e instanceof Error ? e.message : 'Done failed')
    } finally {
      setCommitmentPending((current) => {
        const next = { ...current }
        delete next[item.id]
        return next
      })
    }
  }

  const runCommitmentGroupDone = async (
    groupId: string | null | undefined,
    items: CommitmentTopicItem[],
  ) => {
    if (items.length === 0) return
    const ids = items.map((item) => item.id)
    setCommitmentPending((current) => {
      const next = { ...current }
      for (const id of ids) next[id] = 'done'
      return next
    })
    setCommitmentError(null)
    const staged = items.map((item) => stageCommitmentState(qc, { action: 'done', commitmentId: item.id }))
    try {
      if (!groupId) throw new Error('Commitment group is missing its source id')
      await completeCommitmentGroup(groupId)
      staged.forEach((change) => change.settle())
      void invalidateCommitmentRows()
    } catch (e) {
      staged.forEach((change) => change.rollback())
      setCommitmentError(e instanceof Error ? e.message : 'Done failed')
    } finally {
      setCommitmentPending((current) => {
        const next = { ...current }
        for (const id of ids) delete next[id]
        return next
      })
    }
  }

  function openCommitmentFollowup(item: CommitmentTopicItem, chooseJob = false) {
    setChooseFollowupJob(chooseJob)
    setCommitmentFollowupFor(item)
  }

  const runCommitmentSetFollowup = async (item: CommitmentTopicItem, wakeAt: number, scope: CommitmentFollowupScope) => {
    setCommitmentFollowupFor(null)
    setCommitmentPending((current) => ({ ...current, [item.id]: 'setfu' }))
    setCommitmentError(null)
    try {
      await applyCommitmentAction({
        action: 'setfu',
        commitmentId: item.id,
        scope,
        value: formatLocalDateTime(new Date(wakeAt * 1000)),
      }, qc)
      void invalidateCommitmentRows()
    } catch (e) {
      setCommitmentError(e instanceof Error ? e.message : 'Follow-up update failed')
    } finally {
      setCommitmentPending((current) => {
        const next = { ...current }
        delete next[item.id]
        return next
      })
    }
  }

  if (compact) return (
    <button
      type="button"
      data-topic-id={topic.id}
      onClick={() => { startPrefetch(); onOpen() }}
      onPointerDown={startPrefetch}
      onMouseEnter={startPrefetch}
      onFocus={startPrefetch}
      title={displayTitle}
      className="flex min-h-11 w-full items-center gap-3 border-b border-stone-200/70 px-3 py-2 text-left hover:bg-stone-100/70 dark:border-stone-800/70 dark:hover:bg-stone-800/40"
    >
      {leadingBadge}
      <span className={clsx('min-w-0 flex-1 truncate text-sm', topicPrimaryTextTone)}>{displayTitle}</span>
      {topicHasUnreadMessages && <span className="shrink-0 rounded-full bg-blue-600 px-1.5 py-0.5 text-xs text-white" aria-label={`${unread} unread messages`}>{unread > 99 ? '99+' : unread}</span>}
    </button>
  )

  return (
    <div
      data-topic-id={topic.id}
      data-topic-source-kind={topic.source_kind || undefined}
      data-topic-source-task={topic.source_task_id || undefined}
      className={clsx(
        'group relative flex items-start gap-2 pl-3 pr-2 py-[5px] transition-colors',
        'border-b border-stone-200/70 dark:border-stone-800/70',
        'hover:bg-stone-100/70 dark:hover:bg-stone-800/40',
        selected && 'bg-blue-50 dark:bg-blue-950/40 ring-1 ring-inset ring-blue-500/50',
        topicHasUnreadMessages && !selected && 'bg-blue-50/70 dark:bg-blue-950/30',
        section === 'done' && !hasDraft && 'opacity-60',
        section === 'snoozed' && !hasDraft && 'bg-amber-50/75 dark:bg-amber-950/20 border-l-4 border-l-amber-500 pl-2',
        hasDraft && 'bg-amber-100 dark:bg-amber-900/40 border-l-4 border-l-amber-500 pl-2',
      )}
      {...longPressBind}
    >
      {showTodoistHierarchy && (
        <div
          className="flex shrink-0 items-center self-start"
          style={{ width: `${20 + todoistTreeIndent}px` }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {todoistTreeIndent > 0 && (
            <span className="shrink-0" style={{ width: `${todoistTreeIndent}px` }} aria-hidden />
          )}
          {todoistHierarchy?.hasChildren ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); todoistHierarchy.onToggleExpanded?.() }}
              aria-label={`${todoistHierarchy.expanded ? 'Collapse subtasks' : 'Expand subtasks'} for ${displayTitle}`}
              title={todoistHierarchy.expanded ? 'Collapse subtasks' : 'Expand subtasks'}
              className="grid h-8 w-5 place-items-center rounded-md text-stone-400 transition-colors hover:bg-stone-200 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 dark:hover:bg-stone-700 dark:hover:text-stone-100"
            >
              {todoistHierarchy.expanded
                ? <ChevronDown className="h-3.5 w-3.5" />
                : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          ) : (
            <span className="h-8 w-5 shrink-0" aria-hidden />
          )}
        </div>
      )}
      {leadingBadge && (
        <div className="flex shrink-0 items-center self-start pt-[7px]">{leadingBadge}</div>
      )}
      {/* Phone-primary action: ONE always-visible button per row on the LEFT
          (Close / Wake / Reopen). Snooze and the rest live inside the open task
          (and the desktop kebab) — on the phone Mark wants the row to carry only
          this single button, no long-press. pointerdown is stopped so the tap
          never starts the row's long-press multi-select. */}
      {showInlineQuickActions && !archiveStyle && !selectionMode && !renaming && (
        <div
          className="flex shrink-0 items-center self-start"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {section === 'active' && (
            <RowStateButton
              label="Mark done"
              onClick={async () => {
                setOptimisticDone(true)
                const completed = await onMarkDone(topic)
                if (!completed) setOptimisticDone(false)
              }}
              icon={optimisticDone
                ? <CircleCheck className="h-[22px] w-[22px] text-emerald-600 dark:text-emerald-400" />
                : <Circle className="h-[22px] w-[22px]" />}
            />
          )}
          {section === 'snoozed' && (
            <RowStateButton
              label="Wake now"
              hoverTone="amber"
              busy={snoozeTopic.isPending}
              onClick={() =>
                snoozeTopic.mutate({ topic_id: topic.id, wake_at: Math.floor(Date.now() / 1000) + 1 })
              }
              icon={<Clock className="h-[22px] w-[22px] text-amber-500" />}
            />
          )}
          {section === 'done' && (
            <RowStateButton
              label="Reopen"
              onClick={() => onReopen(topic)}
              icon={<CircleCheck className="h-[22px] w-[22px] text-emerald-600 dark:text-emerald-400" />}
            />
          )}
        </div>
      )}
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false
            return
          }
          if (selectionMode) {
            if (canSelect) onToggleSelected?.()
            return
          }
          startPrefetch()
          onOpen()
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return
          e.preventDefault()
          if (selectionMode) {
            if (canSelect) onToggleSelected?.()
            return
          }
          startPrefetch()
          onOpen()
        }}
        onPointerDown={selectionMode ? undefined : startPrefetch}
        onTouchStart={selectionMode ? undefined : startPrefetch}
        onMouseEnter={selectionMode ? undefined : startPrefetch}
        onFocus={selectionMode ? undefined : startPrefetch}
        className="flex-1 min-w-0 text-left flex items-start gap-2"
        aria-label={
          selectionMode && canSelect
            ? `${selected ? 'Unselect' : 'Select'} topic: ${displayTitle}`
            : `Open topic: ${displayTitle}${topicHasUnreadMessages ? `, ${unread} unread` : ''}`
        }
      >
        {selectionMode && canSelect && (
          <span
            className={clsx(
              'mt-0.5 grid place-items-center w-5 h-5 rounded-full border shrink-0',
              selected
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'border-stone-300 dark:border-stone-600 text-transparent',
            )}
            aria-hidden
          >
            <CheckSquare className="w-3.5 h-3.5" />
          </span>
        )}
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-1.5 mb-0.5">
              {!showInlineQuickActions && section === 'snoozed' && <Clock className="mt-0.5 w-3 h-3 text-amber-500 shrink-0" />}
              {!showInlineQuickActions && section === 'done' && <CheckSquare className="mt-0.5 w-3 h-3 text-emerald-500 shrink-0" />}
              {renaming ? (
                <input
                  autoFocus
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename()
                    if (e.key === 'Escape') { setRenaming(false); setRenameDraft(topic.title || '') }
                  }}
                  className="flex-1 bg-white dark:bg-stone-800 border border-stone-300 dark:border-stone-600 rounded px-2 py-0.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="min-w-0 flex-1">
                  {showCommitmentItems && commitmentItems.length === 1 ? (
                    <CommitmentItemLine
                      item={commitmentItems[0]}
                      pending={commitmentPending[commitmentItems[0].id]}
                      primary
                      includePerson
                      primaryTextTone={topicPrimaryTextTone}
                      onDone={() => void runCommitmentDone(commitmentItems[0])}
                      onSetFollowup={() => openCommitmentFollowup(commitmentItems[0])}
                    />
                  ) : (
                    <div className="flex min-w-0 items-start gap-1.5">
                      {showCommitmentItems && commitmentItems.length > 1 && (
                        <CommitmentDoneButton
                          label={displayTitle}
                          all
                          pending={commitmentItems.some((item) => commitmentPending[item.id] === 'done')}
                          onDone={() => void runCommitmentGroupDone(topic.source_task_id, commitmentItems)}
                        />
                      )}
                      <span
                        className={clsx(
                          'min-w-0 flex-1 text-sm leading-snug break-words',
                          topicPrimaryTextTone,
                          showFullTodoistTaskTitle ? 'block whitespace-pre-wrap' : 'line-clamp-2',
                        )}
                        data-testid="topic-row-primary-text"
                      >
                        {displayTitle}
                      </span>
                      {showCommitmentItems && commitmentItems.length > 1 && (
                      <CommitmentFollowupButton
                        label={displayTitle}
                        followupAt={commitmentItems[0].followup_at}
                        pending={commitmentPending[commitmentItems[0].id] === 'setfu'}
                        onSetFollowup={() => openCommitmentFollowup(commitmentItems[0], true)}
                      />
                      )}
                    </div>
                  )}
                  <CommitmentFollowupBadge items={commitmentItems} closed={topic.closed} snoozedUntil={topic.snoozed_until} />
                  {(source || hasDraft) && (
                    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5">
                      {source && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-200 dark:bg-stone-700 text-[10px] uppercase tracking-wide text-stone-600 dark:text-stone-300 shrink-0"
                          title={source}
                        >
                          {source}
                        </span>
                      )}
                      {hasDraft && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wide font-bold shrink-0 bg-amber-600 dark:bg-amber-500 text-white shadow-sm ring-1 ring-amber-700/40 dark:ring-amber-300/40"
                          title="Unsent draft saved for this topic"
                        >
                          <PencilLine className="w-3 h-3" />
                          Draft
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {isEmailTopic && emailReceivedTs !== null && (
              <div
                className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[12px] leading-tight text-stone-500 dark:text-stone-400"
                data-testid="email-topic-meta"
              >
                <span
                  className="shrink-0 font-medium tabular-nums"
                  title={fullDateTimeLabel(emailReceivedTs)}
                >
                  {shortDateTimeLabel(emailReceivedTs)}
                </span>
                {emailPreview && !hasDraft && (
                  <>
                    <span className="shrink-0 text-stone-400/70 dark:text-stone-500" aria-hidden>
                      &middot;
                    </span>
                    <span className="truncate">{emailPreview}</span>
                  </>
                )}
              </div>
            )}
            {hasDraft && draft ? (
              <div className="text-[13px] text-amber-900 dark:text-amber-200 truncate flex items-center gap-1.5 font-medium">
                <PencilLine className="w-3.5 h-3.5 shrink-0" aria-hidden />
                <span className="font-bold shrink-0 uppercase tracking-wide text-[11px]">Draft</span>
                <span className="truncate italic">{previewDraft(draft)}</span>
              </div>
            ) : null}
            {showCommitmentItems && commitmentItems.length > 1 && !renaming && (
              <div
                className="ml-8 mt-1 space-y-0.5"
                data-testid="commitment-topic-items"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {commitmentItems.map((item, index) => (
                  <CommitmentItemLine
                    key={item.id}
                    item={item}
                    fallbackNumber={index + 1}
                    pending={commitmentPending[item.id]}
                    showFollowupButton={false}
                    onDone={() => void runCommitmentDone(item)}
                    onSetFollowup={() => openCommitmentFollowup(item)}
                  />
                ))}
              </div>
            )}
            {updateTopic.error && (
              <div role="alert" className="mt-1 text-[11px] leading-tight text-red-600 dark:text-red-300">
                {updateTopic.error.message}
              </div>
            )}
            {commitmentError && showCommitmentItems && (
              <div className="mt-1 text-[11px] leading-tight text-red-600 dark:text-red-300">
                {commitmentError}
              </div>
            )}
            {section === 'snoozed' && wakeAt ? (
              <SnoozedWakeMeta wakeAt={wakeAt} />
            ) : null}
          </div>
      </div>

      {/* Time-since (blue-highlighted when unread) for Todoist rows — on the
          RIGHT, opposite the Close button on the left. */}
      {showFullTodoistTaskTitle && (
        <div
          className="flex shrink-0 items-center self-start pt-0.5"
          data-testid="todoist-topic-meta-rail"
        >
          <span
            className={clsx(
              'whitespace-nowrap text-[11px] leading-none',
              topicHasUnreadMessages
                ? 'inline-flex min-w-7 items-center justify-center rounded-full bg-blue-600 px-1.5 py-1 font-bold text-white shadow-sm ring-1 ring-blue-400/60'
                : 'text-stone-500 dark:text-stone-400',
            )}
            title={fullDateTimeLabel(topicAgeTs)}
            data-testid="topic-row-age"
          >
            {topicAge}
          </span>
        </div>
      )}

      {topicHasUnreadMessages && !showFullTodoistTaskTitle && (
        <div
          className="pointer-events-none flex shrink-0 items-center self-center gap-1"
          data-testid="topic-row-meta-rail"
        >
          <span
            className="whitespace-nowrap text-right text-[11px] leading-none text-stone-600 dark:text-stone-400"
            title={fullDateTimeLabel(topicAgeTs)}
            data-testid="topic-row-age"
          >
            {topicAge}
          </span>
          <span
            className="grid min-w-5 h-5 place-items-center rounded-full bg-blue-600 px-1.5 text-[11px] font-bold leading-none text-white shadow-sm ring-1 ring-blue-400/60"
            title={`${unread} unread ${unread === 1 ? 'message' : 'messages'}`}
            aria-label={`${unread} unread ${unread === 1 ? 'message' : 'messages'}`}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        </div>
      )}

      {/* Coding rows: the row action lives on the RIGHT and reads as an
          archive box, not a checkbox — archiving a conversation is putting it
          away, not ticking it off. Same handlers as the left-hand variant. */}
      {showInlineQuickActions && archiveStyle && !selectionMode && !renaming && (
        <div
          className="flex shrink-0 items-center self-center"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {section === 'active' && (
            <RowStateButton
              label="Archive"
              onClick={async () => {
                setOptimisticDone(true)
                const completed = await onMarkDone(topic)
                if (!completed) setOptimisticDone(false)
              }}
              icon={optimisticDone
                ? <ArchiveRestore className="h-[22px] w-[22px] text-emerald-600 dark:text-emerald-400" />
                : <Archive className="h-[22px] w-[22px]" />}
            />
          )}
          {section === 'snoozed' && (
            <RowStateButton
              label="Wake now"
              hoverTone="amber"
              busy={snoozeTopic.isPending}
              onClick={() =>
                snoozeTopic.mutate({ topic_id: topic.id, wake_at: Math.floor(Date.now() / 1000) + 1 })
              }
              icon={<Clock className="h-[22px] w-[22px] text-amber-500" />}
            />
          )}
          {section === 'done' && (
            <RowStateButton
              label="Unarchive"
              onClick={() => onReopen(topic)}
              icon={<ArchiveRestore className="h-[22px] w-[22px] text-emerald-600 dark:text-emerald-400" />}
            />
          )}
        </div>
      )}

      {/* Action button — desktop hover, mobile long-press, both via the
          actions dropdown. */}
      {!selectionMode && (
      <div className={clsx('relative shrink-0', !actionsOpen && 'hidden sm:block')}>
        <button
          onClick={(e) => { e.stopPropagation(); setActionsOpen((v) => !v) }}
          className={clsx(
            'grid place-items-center w-8 h-8 rounded-lg transition-opacity',
            'opacity-0 group-hover:opacity-100',
            actionsOpen && 'opacity-100',
            'hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-500',
          )}
          aria-label="Topic actions"
          aria-expanded={actionsOpen}
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {actionsOpen && (
          <>
            <div
              className="fixed inset-0 z-30"
              onClick={() => setActionsOpen(false)}
            />
            <ul className="absolute right-0 top-full mt-1 z-40 min-w-[200px] bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl shadow-2xl p-1 text-sm">
              {section === 'active' && (
                <>
                  <ActionItem icon={<Bell className="w-3.5 h-3.5" />} onClick={() => { setActionsOpen(false); onSnoozePick() }}>
                    Snooze…
                  </ActionItem>
                  <ActionItem icon={<CheckSquare className="w-3.5 h-3.5" />} onClick={() => {
                    setActionsOpen(false)
                    onMarkDone(topic)
                  }}>
                    Mark done
                  </ActionItem>
                  {topic.source_task_id && onBlockSource && (
                    <ActionItem icon={<Ban className="w-3.5 h-3.5" />} onClick={() => {
                      setActionsOpen(false)
                      onBlockSource(topic)
                    }}>
                      Not for AgentChat
                    </ActionItem>
                  )}
                  <ActionItem icon={<Edit2 className="w-3.5 h-3.5" />} onClick={() => { setActionsOpen(false); setRenaming(true) }}>
                    Rename
                  </ActionItem>
                  <ActionItem icon={<ChevronRight className="w-3.5 h-3.5" />} onClick={() => { setActionsOpen(false); onOpen() }}>
                    Open
                  </ActionItem>
                </>
              )}
              {section === 'snoozed' && (
                <>
                  <ActionItem icon={<BellOff className="w-3.5 h-3.5" />} onClick={() => {
                    setActionsOpen(false)
                    // Quick unsnooze = wake now: send a snooze with wake_at = now+1
                    snoozeTopic.mutate({ topic_id: topic.id, wake_at: Math.floor(Date.now() / 1000) + 1 })
                  }}>
                    Wake now
                  </ActionItem>
                  <ActionItem icon={<Bell className="w-3.5 h-3.5" />} onClick={() => { setActionsOpen(false); onSnoozePick() }}>
                    Reschedule…
                  </ActionItem>
                  <ActionItem icon={<Edit2 className="w-3.5 h-3.5" />} onClick={() => { setActionsOpen(false); setRenaming(true) }}>
                    Rename
                  </ActionItem>
                  <ActionItem icon={<ChevronRight className="w-3.5 h-3.5" />} onClick={() => { setActionsOpen(false); onOpen() }}>
                    Open
                  </ActionItem>
                </>
              )}
              {section === 'done' && (
                <>
                  <ActionItem icon={<RotateCcw className="w-3.5 h-3.5" />} onClick={() => {
                    setActionsOpen(false)
                    onReopen(topic)
                  }}>
                    Reopen
                  </ActionItem>
                  <ActionItem icon={<Edit2 className="w-3.5 h-3.5" />} onClick={() => { setActionsOpen(false); setRenaming(true) }}>
                    Rename
                  </ActionItem>
                  <ActionItem icon={<ChevronRight className="w-3.5 h-3.5" />} onClick={() => { setActionsOpen(false); onOpen() }}>
                    Open
                  </ActionItem>
                </>
              )}
            </ul>
          </>
        )}
      </div>
      )}
      {commitmentFollowupFor && (
        <CommitmentFollowupScopeDialog
          what={chooseFollowupJob ? undefined : commitmentFollowupFor.what}
          who={commitmentFollowupFor.who_owes ?? undefined}
          jobCount={commitmentItems.length}
          jobs={chooseFollowupJob ? commitmentItems.map((item, index) => ({
            id: item.id,
            what: commitmentItemLabel(item, { fallbackNumber: index + 1 }),
          })) : undefined}
          onCancel={() => setCommitmentFollowupFor(null)}
          onPick={(wakeAt, scope, selectedId) => {
            const item = selectedId ? commitmentItems.find((candidate) => candidate.id === selectedId) : commitmentFollowupFor
            if (item) void runCommitmentSetFollowup(item, wakeAt, scope)
          }}
        />
      )}
    </div>
  )
}

function CommitmentItemLine({
  item,
  fallbackNumber = 1,
  pending,
  primary = false,
  includePerson = false,
  primaryTextTone,
  showDoneButton = true,
  showFollowupButton = true,
  onDone,
  onSetFollowup,
}: {
  item: CommitmentTopicItem
  fallbackNumber?: number
  pending?: 'done' | 'setfu'
  primary?: boolean
  includePerson?: boolean
  primaryTextTone?: string
  showDoneButton?: boolean
  showFollowupButton?: boolean
  onDone: () => void
  onSetFollowup: () => void
}) {
  const label = commitmentItemLabel(item, { includePerson, fallbackNumber })
  const dueMeta = commitmentDueMeta(item)
  return (
    <div
      className={clsx(
        'flex min-w-0 items-start gap-1.5 rounded-md',
        primary ? 'py-0' : 'py-0.5',
      )}
      data-testid="commitment-topic-item"
      data-commitment-id={item.id}
    >
      {showDoneButton && (
        <CommitmentDoneButton
          label={label}
          pending={pending === 'done'}
          onDone={onDone}
        />
      )}
      <span
        className={clsx(
          'min-w-0 flex-1 break-words leading-snug line-clamp-2',
          primary
            ? clsx('text-sm', primaryTextTone ?? 'font-semibold text-stone-950 dark:text-stone-50')
            : 'text-[13px] font-medium text-stone-700 dark:text-stone-200',
        )}
        data-testid={primary ? 'topic-row-primary-text' : undefined}
        title={label}
      >
        {label}
      </span>
      {dueMeta && (
        <span
          className={clsx(
            'mt-0.5 shrink-0 whitespace-nowrap text-[11px] font-semibold tabular-nums',
            dueMeta.tone,
          )}
          title={dueMeta.title}
          data-testid="commitment-item-due"
        >
          {dueMeta.label}
        </span>
      )}
      {showFollowupButton && (
        <CommitmentFollowupButton
          label={label}
          followupAt={item.followup_at}
          pending={pending === 'setfu'}
          onSetFollowup={onSetFollowup}
        />
      )}
    </div>
  )
}

function CommitmentDoneButton({
  label,
  all = false,
  pending,
  onDone,
}: {
  label: string
  all?: boolean
  pending: boolean
  onDone: () => void
}) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onDone() }}
      onPointerDown={(e) => e.stopPropagation()}
      disabled={pending}
      aria-label={`${all ? 'Mark all commitments done' : 'Mark commitment done'}: ${label}`}
      title={`${all ? 'Mark all done' : 'Mark done'}: ${label}`}
      className="mt-[1px] grid h-6 w-6 shrink-0 place-items-center rounded-full text-stone-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600 disabled:cursor-wait disabled:opacity-50 dark:hover:bg-emerald-500/15 dark:hover:text-emerald-300"
    >
      {pending
        ? <CircleCheck className="h-[18px] w-[18px] text-emerald-600 dark:text-emerald-400" />
        : <Circle className="h-[18px] w-[18px]" />}
    </button>
  )
}

function CommitmentFollowupButton({
  label,
  followupAt,
  pending,
  onSetFollowup,
}: {
  label: string
  followupAt?: string | null
  pending: boolean
  onSetFollowup: () => void
}) {
  const followupLabel = commitmentDateLabel(followupAt)
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onSetFollowup() }}
      onPointerDown={(e) => e.stopPropagation()}
      disabled={pending}
      aria-label={`Set follow-up for commitment group: ${label}`}
      title={`Set follow-up: ${label}${followupLabel ? ` (currently ${followupLabel})` : ''}`}
      className="mt-[1px] inline-flex h-6 min-w-8 shrink-0 items-center justify-center rounded-md px-1 text-[10px] font-bold tracking-wide text-amber-600 transition-colors hover:bg-amber-50 disabled:cursor-wait disabled:opacity-50 dark:text-amber-300 dark:hover:bg-amber-500/15"
    >
      {pending ? '...' : followupLabel ? `FU ${followupLabel}` : 'Zzz'}
    </button>
  )
}

function commitmentDateLabel(value?: string | null): string | null {
  const match = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  const [, yearText, monthText, dayText] = match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const date = new Date(year, month - 1, day, 12)
  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) return null
  const includeYear = year !== new Date().getFullYear()
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    ...(includeYear ? { year: 'numeric' } : {}),
  }).format(date)
}

function commitmentDueMeta(item: CommitmentTopicItem): {
  label: string
  title: string
  tone: string
} | null {
  const dueLabel = commitmentDateLabel(item.due_date)
  const dueDate = String(item.due_date ?? '').slice(0, 10)
  if (!dueLabel || !/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) return null
  const now = new Date()
  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-')
  const overdue = dueDate < today
  const promised = item.due_date_type === 'promised'
  return {
    label: promised ? `${overdue ? 'Overdue' : 'Due'} ${dueLabel}` : `Est. ${dueLabel}`,
    title: `${promised ? 'Promised date' : 'Estimated date'}: ${dueDate}`,
    tone: overdue && promised
      ? 'text-red-600 dark:text-red-300'
      : 'text-stone-500 dark:text-stone-400',
  }
}

function commitmentItemLabel(
  item: CommitmentTopicItem,
  { includePerson = false, fallbackNumber = 1 }: { includePerson?: boolean; fallbackNumber?: number } = {},
): string {
  const what = item.what?.trim() || '(no commitment text)'
  const numberedWhat = `${item.task_number ?? fallbackNumber}. ${what}`
  if (!includePerson) return numberedWhat
  const person = item.who_owes?.trim() || item.who_email?.trim() || ''
  if (!person || what.toLocaleLowerCase().startsWith(person.toLocaleLowerCase())) {
    return numberedWhat
  }
  return `${person} · ${numberedWhat}`
}

function SnoozedWakeMeta({ wakeAt }: { wakeAt: number }) {
  return (
    <div
      data-testid="snoozed-topic-wake"
      title={`Wakes ${fullDateTimeLabel(wakeAt)}`}
      className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] leading-tight text-amber-700 dark:text-amber-300"
    >
      <Bell className="h-3 w-3 shrink-0" aria-hidden />
      <span className="whitespace-nowrap font-medium">
        Wakes {relativeTimeFromNow(wakeAt)}
      </span>
      <span className="text-amber-500/80 dark:text-amber-400/80" aria-hidden>
        &middot;
      </span>
      <span className="min-w-0 truncate">
        {shortDateTimeLabel(wakeAt)}
      </span>
    </div>
  )
}

/** Bare, checkbox-style state toggle on the LEFT of a Todoist row: an empty
 *  circle on an active task (tap → marks done, the circle fills with a tick), a
 *  green tick on a done task (tap → reopen), an amber clock on a snoozed task
 *  (tap → wake). No box — it reads as a checkbox, not a button. The wrapper
 *  stops pointerdown so a tap never starts the row's long-press select; click
 *  propagation is stopped so it never falls through to "open topic". */
function RowStateButton({
  icon, label, onClick, busy = false, hoverTone = 'emerald',
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  busy?: boolean
  hoverTone?: 'emerald' | 'amber'
}) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick() }}
      disabled={busy}
      aria-label={label}
      title={label}
      className={clsx(
        'grid h-8 w-8 shrink-0 place-items-center rounded-full text-stone-400',
        'transition-colors disabled:cursor-wait disabled:opacity-50',
        'focus-visible:outline-none focus-visible:ring-2',
        hoverTone === 'emerald'
          ? 'hover:bg-emerald-50 hover:text-emerald-600 focus-visible:ring-emerald-400 dark:hover:bg-emerald-500/15 dark:hover:text-emerald-300'
          : 'hover:bg-amber-50 hover:text-amber-600 focus-visible:ring-amber-400 dark:hover:bg-amber-500/15 dark:hover:text-amber-300',
      )}
    >
      {icon}
    </button>
  )
}

function ActionItem({
  icon, onClick, children,
}: { icon: React.ReactNode; onClick: () => void; children: React.ReactNode }) {
  return (
    <li>
      <button
        onClick={onClick}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-left rounded-lg hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
      >
        <span className="text-stone-500">{icon}</span>
        {children}
      </button>
    </li>
  )
}
